import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";

export type ChangelogCategory = "feature" | "improvement" | "fix" | "breaking" | "security";

export interface ChangelogEntry {
  id: string;
  version: string;
  released_at: string;
  category: ChangelogCategory;
  title: string;
  description_md: string;
  related_module: string | null;
  related_sprint: string | null;
  is_published: boolean;
  is_highlighted: boolean;
  created_at: string;
  updated_at: string;
}

interface ChangelogFilters {
  category?: ChangelogCategory | "all";
  module?: string;
  only_highlighted?: boolean;
  includeDrafts?: boolean;
}

export function useChangelog(filters: ChangelogFilters = {}) {
  const { isSuperAdmin } = useSuperAdmin();
  const includeDrafts = filters.includeDrafts && isSuperAdmin;

  return useQuery({
    queryKey: ["changelog", filters, includeDrafts],
    queryFn: async () => {
      let query = supabase
        .from("changelog_entries")
        .select("*")
        .order("released_at", { ascending: false });

      if (!includeDrafts) {
        query = query.eq("is_published", true);
      }
      if (filters.category && filters.category !== "all") {
        query = query.eq("category", filters.category);
      }
      if (filters.module) {
        query = query.eq("related_module", filters.module);
      }
      if (filters.only_highlighted) {
        query = query.eq("is_highlighted", true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ChangelogEntry[];
    },
  });
}

export function useChangelogUnreadCount() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["changelog-unread-count", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      // Buscar last_seen_at do usuário
      const { data: seen } = await supabase
        .from("user_changelog_seen")
        .select("last_seen_at")
        .eq("user_id", user!.id)
        .maybeSingle();

      let query = supabase
        .from("changelog_entries")
        .select("id", { count: "exact", head: true })
        .eq("is_published", true);

      if (seen?.last_seen_at) {
        query = query.gt("released_at", seen.last_seen_at);
      }

      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    },
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
}

export function useMarkChangelogAsSeen() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!user?.id) return;
      const { error } = await supabase
        .from("user_changelog_seen")
        .upsert(
          { user_id: user.id, last_seen_at: new Date().toISOString() },
          { onConflict: "user_id" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog-unread-count", user?.id] });
    },
  });
}

// ===== Super admin mutations =====

export interface ChangelogInput {
  id?: string;
  version: string;
  released_at: string;
  category: ChangelogCategory;
  title: string;
  description_md: string;
  related_module?: string | null;
  related_sprint?: string | null;
  is_published: boolean;
  is_highlighted: boolean;
}

export function useUpsertChangelogEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ChangelogInput) => {
      const payload = {
        version: input.version,
        released_at: input.released_at,
        category: input.category,
        title: input.title,
        description_md: input.description_md,
        related_module: input.related_module ?? null,
        related_sprint: input.related_sprint ?? null,
        is_published: input.is_published,
        is_highlighted: input.is_highlighted,
      };
      if (input.id) {
        const { error } = await supabase
          .from("changelog_entries")
          .update(payload)
          .eq("id", input.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("changelog_entries").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog"] });
      qc.invalidateQueries({ queryKey: ["changelog-unread-count"] });
    },
  });
}

export function useDeleteChangelogEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("changelog_entries").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog"] });
      qc.invalidateQueries({ queryKey: ["changelog-unread-count"] });
    },
  });
}

export function useTogglePublishChangelog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_published }: { id: string; is_published: boolean }) => {
      const { error } = await supabase
        .from("changelog_entries")
        .update({ is_published })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog"] });
      qc.invalidateQueries({ queryKey: ["changelog-unread-count"] });
    },
  });
}

export function useToggleHighlightChangelog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_highlighted }: { id: string; is_highlighted: boolean }) => {
      const { error } = await supabase
        .from("changelog_entries")
        .update({ is_highlighted })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });
}
